import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, MessageSquare, Send, LifeBuoy } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

const STATUS_TONES = {
  open: "bg-amber-500/10 text-amber-700 border-amber-500/40",
  in_progress: "bg-primary/10 text-primary border-primary/40",
  resolved: "bg-emerald-500/10 text-emerald-700 border-emerald-500/40",
  closed: "bg-muted text-muted-foreground border-border",
};

const PRIORITY_TONES = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-primary/10 text-primary",
  high: "bg-amber-500/10 text-amber-700",
  urgent: "bg-rose-500/10 text-rose-600",
};

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * DashboardTicketsTab — user-side support inbox.
 * Users can open new tickets, browse their own tickets, and reply on
 * an admin's response. They cannot see other users' tickets or change
 * status/priority.
 */
const PREFILLS = {
  rename_company: {
    labelKey: "tickets.prefill.renameCompanyLabel",
    subjectKey: "tickets.prefill.renameCompanySubject",
    bodyKey: "tickets.prefill.renameCompanyBody",
  },
  data_room_access: {
    labelKey: "tickets.prefill.dataRoomAccessLabel",
    subjectKey: "tickets.prefill.dataRoomAccessSubject",
    bodyKey: "tickets.prefill.dataRoomAccessBody",
  },
  mentor_match: {
    labelKey: "tickets.prefill.mentorMatchLabel",
    subjectKey: "tickets.prefill.mentorMatchSubject",
    bodyKey: "tickets.prefill.mentorMatchBody",
  },
  milestones: {
    labelKey: "tickets.prefill.milestoneLabel",
    subjectKey: "tickets.prefill.milestoneSubject",
    bodyKey: "tickets.prefill.milestoneBody",
  },
  profile_update: {
    labelKey: "tickets.prefill.profileUpdateLabel",
    subjectKey: "tickets.prefill.profileUpdateSubject",
    bodyKey: "tickets.prefill.profileUpdateBody",
  },
  booking_issue: {
    labelKey: "tickets.prefill.bookingIssueLabel",
    subjectKey: "tickets.prefill.bookingIssueSubject",
    bodyKey: "tickets.prefill.bookingIssueBody",
  },
  technical: {
    labelKey: "tickets.prefill.technicalLabel",
    subjectKey: "tickets.prefill.technicalSubject",
    bodyKey: "tickets.prefill.technicalBody",
  },
  program_start: {
    labelKey: "tickets.prefill.programStartLabel",
    subjectKey: "tickets.prefill.programStartSubject",
    bodyKey: "tickets.prefill.programStartBody",
  },
};

export function DashboardTicketsTab() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", priority: "medium" });
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/tickets");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Deep links such as /dashboard?tab=tickets&prefill=rename_company open the
  // composer with the request already written out.
  useEffect(() => {
    const key = searchParams.get("prefill");
    const preset = key && PREFILLS[key];
    if (!preset) return;
    setForm({
      subject: t(preset.subjectKey, { defaultValue: "Company name change request" }),
      body: t(preset.bodyKey, { defaultValue: "" }),
      priority: "medium",
    });
    setShowNew(true);
    const next = new URLSearchParams(searchParams);
    next.delete("prefill");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const applyPrefill = (key) => {
    const preset = PREFILLS[key];
    if (!preset) return;
    setForm({
      subject: t(preset.subjectKey),
      body: t(preset.bodyKey),
      priority: "medium",
    });
    setShowNew(true);
  };

  const submitNew = async (e) => {
    e?.preventDefault?.();
    if (!form.subject.trim() || !form.body.trim()) return;
    setCreating(true);
    try {
      await client.post("/tickets", form);
      toast.success(t("tickets.created"));
      setForm({ subject: "", body: "", priority: "medium" });
      setShowNew(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  const openTicket = async (ticket_id) => {
    try {
      const res = await client.get(`/tickets/${ticket_id}`);
      setActive(res.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <div className="space-y-5" data-testid="dashboard-tickets-tab">
      <Card className="rounded-2xl border border-border p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <LifeBuoy className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold">{t("tickets.userTitle")}</h3>
            <p className="text-xs text-muted-foreground">{t("tickets.userSubtitle")}</p>
          </div>
          <Button
            className="btn-brand rounded-xl gap-1"
            onClick={() => setShowNew((s) => !s)}
            data-testid="tickets-new-toggle"
          >
            <Plus className="h-4 w-4" /> {t("tickets.new")}
          </Button>
        </div>

        <div className="mt-4 rounded-xl border border-dashed border-border bg-muted/20 p-3" data-testid="tickets-common-questions">
          <p className="text-xs font-medium">{t("tickets.commonQuestions")}</p>
          <p className="text-[11px] text-muted-foreground">{t("tickets.commonQuestionsHint")}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(PREFILLS).map(([key, preset]) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPrefill(key)}
                className="rounded-full border border-border bg-background px-3 py-1.5 text-[11px] font-medium transition-colors hover:border-primary hover:text-primary"
                data-testid={`tickets-quick-${key}`}
              >
                {t(preset.labelKey)}
              </button>
            ))}
          </div>
        </div>

        {showNew && (
          <form onSubmit={submitNew} className="grid gap-3 mt-4" data-testid="tickets-new-form">
            <Input
              placeholder={t("tickets.subjectPlaceholder")}
              className="rounded-xl"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              data-testid="tickets-new-subject"
            />
            <Textarea
              placeholder={t("tickets.bodyPlaceholder")}
              className="rounded-xl min-h-[150px]"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              data-testid="tickets-new-body"
            />
            <div className="flex items-center gap-2">
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="rounded-xl w-[160px]" data-testid="tickets-new-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t("tickets.priorityLow")}</SelectItem>
                  <SelectItem value="medium">{t("tickets.priorityMedium")}</SelectItem>
                  <SelectItem value="high">{t("tickets.priorityHigh")}</SelectItem>
                  <SelectItem value="urgent">{t("tickets.priorityUrgent")}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                type="submit"
                className="btn-brand rounded-xl gap-1 ml-auto"
                disabled={creating || !form.subject.trim() || !form.body.trim()}
                data-testid="tickets-new-submit"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t("tickets.send")}
              </Button>
            </div>
          </form>
        )}
      </Card>

      <Card className="rounded-2xl border border-border p-5 space-y-3" data-testid="tickets-list-card">
        <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          {t("tickets.myTickets")} · {items.length}
        </h3>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2" data-testid="tickets-empty">{t("tickets.empty")}</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((tk) => (
              <li key={tk.ticket_id} data-testid={`tickets-item-${tk.ticket_id}`}>
                <button
                  type="button"
                  className="w-full text-left py-3 hover:bg-muted/30 rounded-lg px-2 transition-colors"
                  onClick={() => openTicket(tk.ticket_id)}
                  data-testid={`tickets-open-${tk.ticket_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-sm truncate">{tk.subject}</p>
                        {tk.unread_by_user && (
                          <span className="inline-flex h-2 w-2 rounded-full bg-primary" title="Admin replied" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-1 text-[11px] text-muted-foreground">
                        <span>{fmtDateTime(tk.updated_at)}</span>
                        <span>·</span>
                        <span>{tk.message_count} {t("tickets.replies")}</span>
                      </div>
                    </div>
                    <Badge variant="outline" className={`rounded-full text-[10px] capitalize ${STATUS_TONES[tk.status]}`}>
                      {t(`tickets.status.${tk.status}`)}
                    </Badge>
                    <Badge className={`rounded-full text-[10px] capitalize ${PRIORITY_TONES[tk.priority]}`}>
                      {t(`tickets.priority${tk.priority.charAt(0).toUpperCase() + tk.priority.slice(1)}`)}
                    </Badge>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <TicketDialog
        ticket={active}
        onClose={() => { setActive(null); load(); }}
        canManage={false}
        onSent={() => openTicket(active.ticket_id)}
      />
    </div>
  );
}

/**
 * Shared ticket dialog for both user and admin views.
 * `canManage` unlocks status/priority editing for admins.
 */
export function TicketDialog({ ticket, onClose, canManage, onSent, onManaged }) {
  const { t } = useTranslation();
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(ticket?.status || "open");
  const [priority, setPriority] = useState(ticket?.priority || "medium");
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    setReply("");
    setStatus(ticket?.status || "open");
    setPriority(ticket?.priority || "medium");
  }, [ticket?.ticket_id, ticket?.status, ticket?.priority]);

  const send = async () => {
    if (!reply.trim() || !ticket) return;
    setSending(true);
    try {
      await client.post(`/tickets/${ticket.ticket_id}/messages`, { body: reply });
      setReply("");
      toast.success(t("tickets.replySent"));
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSending(false);
    }
  };

  const saveMeta = async (patch) => {
    if (!ticket) return;
    setSavingMeta(true);
    try {
      await client.patch(`/admin/tickets/${ticket.ticket_id}`, patch);
      toast.success(t("tickets.updated"));
      onManaged && onManaged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSavingMeta(false);
    }
  };

  const open = !!ticket;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle className="pr-8">
            {ticket?.subject}
          </DialogTitle>
        </DialogHeader>
        {ticket && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              <span>{ticket.user_name || t("common.member", { defaultValue: "Member" })}</span>
              <span>·</span>
              <span>{fmtDateTime(ticket.created_at)}</span>
              {canManage ? (
                <>
                  <Select value={status} onValueChange={(v) => { setStatus(v); saveMeta({ status: v }); }} disabled={savingMeta}>
                    <SelectTrigger className="ml-auto rounded-lg h-8 w-[140px] text-xs" data-testid="ticket-dialog-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["open", "in_progress", "resolved", "closed"].map((s) => (
                        <SelectItem key={s} value={s}>{t(`tickets.status.${s}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={priority} onValueChange={(v) => { setPriority(v); saveMeta({ priority: v }); }} disabled={savingMeta}>
                    <SelectTrigger className="rounded-lg h-8 w-[120px] text-xs" data-testid="ticket-dialog-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["low", "medium", "high", "urgent"].map((p) => (
                        <SelectItem key={p} value={p}>{t(`tickets.priority${p.charAt(0).toUpperCase() + p.slice(1)}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              ) : (
                <>
                  <Badge variant="outline" className={`ml-auto rounded-full text-[10px] capitalize ${STATUS_TONES[ticket.status]}`}>
                    {t(`tickets.status.${ticket.status}`)}
                  </Badge>
                  <Badge className={`rounded-full text-[10px] capitalize ${PRIORITY_TONES[ticket.priority]}`}>
                    {t(`tickets.priority${ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}`)}
                  </Badge>
                </>
              )}
            </div>

            <div className="rounded-xl border border-border p-3 bg-muted/20" data-testid="ticket-dialog-body">
              <p className="text-sm whitespace-pre-wrap">{ticket.body}</p>
            </div>

            <div className="space-y-3">
              {(ticket.messages || []).map((m) => {
                const isAdmin = m.author_role === "admin";
                return (
                  <div
                    key={m.message_id}
                    className={`rounded-xl border p-3 ${isAdmin ? "border-primary/40 bg-primary/5" : "border-border bg-muted/10"}`}
                    data-testid={`ticket-dialog-message-${m.message_id}`}
                  >
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                      <span className={`font-medium ${isAdmin ? "text-primary" : ""}`}>
                        {isAdmin ? t("tickets.adminReply") : (m.author_name || t("tickets.you"))}
                      </span>
                      <span>·</span>
                      <span>{fmtDateTime(m.created_at)}</span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                  </div>
                );
              })}
            </div>

            {ticket.status !== "closed" ? (
              (() => {
                const adminReplied = (ticket.messages || []).some((m) => (m || {}).author_role === "admin");
                const userMayReply = canManage || adminReplied;
                return (
                  <div className="space-y-2 pt-2 border-t border-border">
                    {!userMayReply && (
                      <p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2" data-testid="ticket-dialog-reply-locked">
                        {t("tickets.replyLocked")}
                      </p>
                    )}
                    <Textarea
                      placeholder={userMayReply ? t("tickets.replyPlaceholder") : t("tickets.replyLockedPlaceholder")}
                      className="rounded-xl min-h-[80px]"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      disabled={!userMayReply || sending}
                      data-testid="ticket-dialog-reply-input"
                    />
                    <div className="flex justify-end">
                      <Button
                        className="btn-brand rounded-xl gap-1"
                        onClick={send}
                        disabled={sending || !reply.trim() || !userMayReply}
                        data-testid="ticket-dialog-reply-send"
                      >
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {t("tickets.send")}
                      </Button>
                    </div>
                  </div>
                );
              })()
            ) : (
              <p className="text-xs text-muted-foreground text-center py-2 border-t border-border">
                {t("tickets.closedNote")}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DashboardTicketsTab;
